import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import AdornedCurrencyInput from '@/components/common/AdornedCurrencyInput';
import AccountTagsAutocomplete from '@/components/common/AccountTagsAutocomplete';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import CategoryUtility, { ClassificationItem } from '../categories.utility';
import { Classification } from '@/store/classificationsApi';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  RealEstateDetailsFormState,
  RealEstateDetailsFormValues,
} from './useRealEstateDetailsForm';
import { useLocalization } from '@/hooks/useLocalization';
import { useAccountTagCatalogOptions } from '@/hooks/useAccountTagCatalogOptions';
import { useAttributeAccountsToViewingTag } from '@/hooks/useAttributeAccountsToViewingTag';
import { mergeSelectedViewingTagOption } from '@/modules/portfolio/accountTags/accountTagCatalog';
import { nextSelectedTagIdsForViewingTagSeed } from '@/utils/viewingTagAccountAttribution';
import ZillowLogo from '@/assets/images/zillow-logo.png';
import { useLazyGetZEstimateQuery, parseAddress } from '@/store/propertyApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { CopyToClipboard } from '@/components/CopyToClipboard';

interface RealEstateDetailsFormProps {
  initialValues: RealEstateDetailsFormValues;
  selectedClassification: ClassificationItem;
  classifications: Classification[];
  onFormChange: (state: RealEstateDetailsFormState) => void;
}

const RealEstateDetailsForm = ({
  initialValues,
  selectedClassification,
  classifications,
  onFormChange,
}: RealEstateDetailsFormProps) => {
  const { t } = useLocalization();
  const { options: catalogTagOptions, isLoading: isLoadingTags } =
    useAccountTagCatalogOptions();
  const { attributableTag, attributableTagId } =
    useAttributeAccountsToViewingTag();
  const hasSeededViewingTagRef = useRef(false);

  const tagOptions = useMemo(
    () => mergeSelectedViewingTagOption(catalogTagOptions, attributableTag),
    [catalogTagOptions, attributableTag],
  );

  const [zestimateValue, setZestimateValue] = useState<string>('$0');
  const [zestimateZpid, setZestimateZpid] = useState<string>('');
  const [triggerZEstimate, { isLoading: isZEstimateLoading }] =
    useLazyGetZEstimateQuery();

  // Update the handleGetZestimate function:
  const handleGetZestimate = async (fullAddress: string | undefined) => {
    setZestimateValue(
      t('addAccountModal.accountDetails.realEstate.fetchingZestimate'),
    );
    setZestimateZpid('');

    if (!fullAddress?.trim()) {
      return;
    }

    const parsedAddress = parseAddress(fullAddress.trim());

    if (!parsedAddress) {
      setZestimateValue('Invalid address format');
      return;
    }

    try {
      const result = await triggerZEstimate(parsedAddress).unwrap();
      const { zestimate, zpid } = result.value;

      setZestimateValue(
        zestimate
          ? formatNumberToCurrency(Number(zestimate))
          : 'No estimate available',
      );
      setZestimateZpid(zpid);

      await realEstateForm.setFieldValue('Zpid', zpid);
    } catch {
      setZestimateValue('Error fetching estimate');
    }
  };

  const subCategories = CategoryUtility.getSubcategories(
    selectedClassification,
    classifications,
  );

  const validationSchema = Yup.object({
    Name: Yup.string().required(t('addAccountModal.accountDetails.required')),
    Notes: Yup.string().optional(),
    Address: Yup.string().optional(),
    PropertyValue: Yup.number().required(
      t('addAccountModal.accountDetails.required'),
    ),
    AccountSubType: Yup.number().when([], {
      is: () => subCategories.length > 0,
      then: (schema) =>
        schema.required(t('addAccountModal.accountDetails.required')),
      otherwise: (schema) => schema.notRequired(),
    }),
    Zpid: Yup.string().optional(),
  });

  const realEstateForm = useFormik({
    initialValues: {
      ...initialValues,
      ClassificationId: selectedClassification.ClassificationId,
      ParentId: selectedClassification.ParentId,
      SelectedTagIds:
        initialValues.SelectedTagIds.length > 0
          ? initialValues.SelectedTagIds
          : attributableTagId != null
            ? [attributableTagId]
            : [],
    },
    validationSchema,
    onSubmit: () => {},
    initialErrors: {
      Name: t('addAccountModal.accountDetails.required'),
    }, // Not visible to the user, but disables the "Continue" button on load.
    validateOnMount: true,
    validateOnChange: true,
  });

  useEffect(() => {
    const { nextIds, seeded } = nextSelectedTagIdsForViewingTagSeed({
      hasSeeded: hasSeededViewingTagRef.current,
      viewingTagId: attributableTagId,
      currentIds: realEstateForm.values.SelectedTagIds,
    });
    hasSeededViewingTagRef.current = seeded;
    if (nextIds != null) {
      void realEstateForm.setFieldValue('SelectedTagIds', nextIds, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once from viewing-tag filter
  }, [
    attributableTagId,
    realEstateForm.values.SelectedTagIds,
    realEstateForm.setFieldValue,
  ]);

  // Validation runs every time the form changes.
  useEffect(() => {
    onFormChange({
      values: realEstateForm.values,
      isValid: realEstateForm.isValid,
    });
  }, [realEstateForm.values, realEstateForm.isValid, onFormChange]);

  const handleCurrencyChange = async (
    field: 'PropertyValue',
    value: string | undefined,
  ) => {
    await realEstateForm.setFieldValue(field, value);
    await realEstateForm.validateForm(); // Force validation immediately
  };

  const handleSelectChange = async (
    field: 'AccountSubType',
    value: number | undefined,
  ) => {
    await realEstateForm.setFieldValue(field, value);
    await realEstateForm.validateForm(); // Manually validate form
  };

  const handleAddressChange = (value: string) => {
    void realEstateForm.setFieldValue('Address', value);
    // Reset zestimate and property value.
    setZestimateValue('$0');
    setZestimateZpid('');
    void handleCurrencyChange('PropertyValue', '');
  };

  return (
    <form onSubmit={realEstateForm.handleSubmit}>
      <Box sx={{ marginBottom: 4 }}>
        <Grid
          container
          rowSpacing={3}
          columnSpacing={3}
        >
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label={t('addAccountModal.accountDetails.accountName.label')}
              placeholder={t(
                'addAccountModal.accountDetails.realEstate.enterPropertyName',
              )}
              required
              error={
                realEstateForm.touched.Name && !!realEstateForm.errors.Name
              }
              {...realEstateForm.getFieldProps('Name')}
            />
            {realEstateForm.touched.Name && realEstateForm.errors.Name && (
              <FormHelperText error>
                {realEstateForm.errors.Name}
              </FormHelperText>
            )}
          </Grid>

          {/* ONLY if sub-types */}
          {subCategories.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <FormControl
                fullWidth
                required
                error={
                  realEstateForm.touched.AccountSubType &&
                  Boolean(realEstateForm.errors.AccountSubType)
                }
              >
                <InputLabel id='subtype-label'>
                  {t('addAccountModal.accountDetails.accountSubtype.label', {
                    name: selectedClassification.Name,
                  })}
                </InputLabel>
                <Select
                  labelId='subtype-label'
                  id='subtype'
                  label={t(
                    'addAccountModal.accountDetails.accountSubtype.label',
                    {
                      name: selectedClassification.Name,
                    },
                  )}
                  {...realEstateForm.getFieldProps('AccountSubType')}
                  onChange={(event) => {
                    void handleSelectChange(
                      'AccountSubType',
                      Number(event.target.value),
                    );
                  }}
                >
                  {subCategories.map((subCategory) => (
                    <MenuItem
                      value={subCategory.ClassificationId}
                      key={subCategory.ClassificationId}
                    >
                      {subCategory.Name}
                    </MenuItem>
                  ))}
                </Select>
                {realEstateForm.touched.AccountSubType &&
                  Boolean(realEstateForm.errors.AccountSubType) && (
                    <FormHelperText error>
                      {realEstateForm.errors.AccountSubType}
                    </FormHelperText>
                  )}
              </FormControl>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <AddressAutocomplete
              fullWidth
              label={t('addAccountModal.accountDetails.realEstate.address')}
              placeholder={t(
                'addAccountModal.accountDetails.realEstate.addressPlaceholder',
              )}
              value={realEstateForm.values.Address || ''}
              onChange={handleAddressChange}
              onBlur={realEstateForm.getFieldProps('Address').onBlur}
              name='Address'
            />
            {zestimateZpid && (
              <FormHelperText sx={{ mt: 0.5 }}>
                <Link
                  href={`https://www.zillow.com/homedetails/placeholder/${zestimateZpid}_zpid/`}
                  target='_blank'
                  rel='noopener'
                  variant='caption'
                  underline='none'
                >
                  See more details for {realEstateForm.values.Address} on Zillow
                </Link>
              </FormHelperText>
            )}
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                mb: { xs: 2, sm: 0 },
              }}
            >
              <img
                src={ZillowLogo}
                alt='Zillow Logo'
                style={{
                  height: '36px',
                  margin: '8px',
                }}
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 9 }}>
            <Grid
              container
              spacing={2}
              alignItems='center'
              sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
            >
              <Grid size={{ xs: 12, sm: 'auto' }}>
                {/* get zestimate button*/}
                <Button
                  variant='contained'
                  disabled={
                    !realEstateForm.values.Address ||
                    !realEstateForm.values.Address.length
                  }
                  onClick={() =>
                    void handleGetZestimate(realEstateForm.values.Address)
                  }
                  type='button'
                  sx={{
                    mb: { xs: 2, sm: 0 },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {t('addAccountModal.accountDetails.realEstate.getZestimate')}
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 'grow' }}>
                <TextField
                  disabled
                  fullWidth
                  label={t(
                    'addAccountModal.accountDetails.realEstate.currentZestimate',
                  )}
                  value={zestimateValue}
                  placeholder={isZEstimateLoading ? t('loading') : '0'}
                  helperText="Enter a property address to generate the current Zestimate 'as is' value."
                  slotProps={{
                    input: {
                      endAdornment: (
                        <CopyToClipboard
                          disabled={!zestimateZpid}
                          copiedValue={zestimateValue}
                        />
                      ),
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <AdornedCurrencyInput
              fullWidth
              label={t(
                'addAccountModal.accountDetails.realEstate.propertyValue',
              )}
              placeholder={t(
                'addAccountModal.accountDetails.realEstate.propertyValuePlaceholder',
              )}
              value={realEstateForm.values.PropertyValue}
              error={
                realEstateForm.touched.PropertyValue &&
                !!realEstateForm.errors.PropertyValue
              }
              required
              onValueChange={(event) => {
                return void handleCurrencyChange('PropertyValue', event);
              }}
              onBlur={() => {
                void realEstateForm.setFieldTouched('PropertyValue', true);
              }}
            />
            {realEstateForm.touched.PropertyValue &&
              realEstateForm.errors.PropertyValue && (
                <FormHelperText error>
                  {realEstateForm.errors.PropertyValue}
                </FormHelperText>
              )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <AccountTagsAutocomplete
              options={tagOptions}
              selectedTagIds={realEstateForm.values.SelectedTagIds}
              isLoading={isLoadingTags}
              onChange={(tagIds) => {
                void realEstateForm.setFieldValue('SelectedTagIds', tagIds);
              }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label={t('addAccountModal.accountDetails.notes.label')}
              multiline
              placeholder={t(
                'addAccountModal.accountDetails.notes.placeholder',
              )}
              rows={3}
              {...realEstateForm.getFieldProps('Notes')}
            />
          </Grid>
        </Grid>
      </Box>
    </form>
  );
};

export default RealEstateDetailsForm;
